import { useMutation } from '@apollo/client';
import { createContext, PropsWithChildren, useCallback, useContext } from 'react';
import { CustomSession } from '../session/type';
import { AuthorizeWithGoogleResponse, OAUTH_GOOGLE } from './gql';

interface GoogleAuthContextType {
    authorize: (code: string) => Promise<CustomSession>;
}

const GoogleAuthContext = createContext<GoogleAuthContextType | undefined>(undefined);

export const GoogleAuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [authorizeWithGoogle] = useMutation<AuthorizeWithGoogleResponse>(OAUTH_GOOGLE, { fetchPolicy: 'network-only' });

    const authorize = useCallback(
        async (code: string): Promise<CustomSession> => {
            const result = await authorizeWithGoogle({
                variables: { token: code },
            });
            const data = result.data?.authenticateWithGoogle;
            if (data) {
                if (data.__typename === 'AuthenticateWithGoogleSuccess' || data.__typename === 'SignUpWithGoogleSuccess') {
                    return { token: data.sessionToken, user: data.item };
                } else if (data.__typename === 'AuthenticateWithGoogleFailure') {
                    throw new Error(data.message);
                }
            }
            throw new Error('Unexpected error: No data returned from Google authentication');
        },
        [authorizeWithGoogle],
    );

    return (
        <GoogleAuthContext.Provider
            value={{
                authorize,
            }}
        >
            {children}
        </GoogleAuthContext.Provider>
    );
};

export const useGoogleAuth = (): GoogleAuthContextType => {
    const context = useContext(GoogleAuthContext);
    if (!context) {
        throw new Error('useGoogleAuth must be used within an GoogleAuthProvider');
    }

    return context;
};
